import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { DashboardCard, ReportDefinition, ReportModule } from '@goyone/shared';
import { api } from '../lib/api';
import { Icon } from './Icon';
import {
  LayoutColumn,
  ProfileLayoutBar,
  ProfileLayoutButton,
  ProfileLayoutProvider,
  Section,
  useProfileLayout,
  type SectionDef,
} from './ProfileLayout';
import { ReportCard } from './ReportCard';

/**
 * لوحةُ تقارير — بطاقاتٌ جاهزة وتقاريرُ نشرها الفريق، في نمطٍ يُحرَّر.
 *
 * البطاقاتُ الجاهزة من الحزمة المشتركة (يفحصها الاختبار)، والمنشورةُ من
 * الباني: من حفظ تقريرًا وقال «انشره على لوحة المبيعات» ظهر هنا لكلّ من
 * يفتحها. وكلُّ بطاقةٍ قسمٌ في نمط الملفّ: تُخفى وتُعاد وتُكبَّر وتُرتَّب
 * بالسحب، والتفضيلُ في المتصفّح كسائر الشاشات.
 *
 * والمدّةُ واحدةٌ للوحة كلِّها: ما لا يخضع لمدّة (لقطاتُ الآن) يُعلمها بنفسه.
 */

interface SavedReportRow {
  id: string;
  name: string;
  description: string | null;
  definition: ReportDefinition;
}

export interface BoardCard {
  key: string;
  title: string;
  hint?: string;
  definition: ReportDefinition;
  wide?: boolean;
  ignoreRange?: boolean;
  savedId?: string;
}

function lastDays(days: number): { from: string; to: string } {
  const now = new Date();
  return {
    from: new Date(now.getTime() - days * 86_400_000).toISOString().slice(0, 10),
    to: now.toISOString().slice(0, 10),
  };
}

/** التقاريرُ المنشورة على لوحةٍ: الرئيسية أو وحدة. */
export function usePublishedReports(scope: { home: true } | { module: ReportModule }) {
  const query = 'home' in scope ? '?home=true' : `?module=${scope.module}`;
  return useQuery({
    queryKey: ['reports', 'saved', 'published', query],
    queryFn: () => api<{ items: SavedReportRow[] }>(`/reports/saved${query}`),
    staleTime: 60_000,
  });
}

export function toBoardCards(
  prebuilt: readonly DashboardCard[],
  published: SavedReportRow[],
): BoardCard[] {
  return [
    ...prebuilt.map((c, i) => ({
      key: `p${i}`,
      title: c.title,
      hint: c.hint,
      definition: c.definition as ReportDefinition,
      wide: c.wide,
      ignoreRange: c.ignoreRange,
    })),
    ...published.map((r) => ({
      key: `s${r.id}`,
      title: r.name,
      hint: r.description ?? undefined,
      definition: r.definition,
      /* المحفوظُ بنطاقه هو: صاحبُه اختار مدّته حين حفظه. */
      ignoreRange: Boolean(r.definition.from || r.definition.to),
      savedId: r.id,
    })),
  ];
}

export function DashboardBoard({
  storageKey,
  cards,
  addTo,
  title,
}: {
  storageKey: string;
  cards: BoardCard[];
  /** مسارُ الباني مع الوحدة، لزرّ «تقرير جديد». */
  addTo: string;
  title?: string;
}) {
  const [days, setDays] = useState('365');
  const range = lastDays(Number(days));

  /*
   * التوزيعُ الافتراضيّ: العريضُ في العمود الكامل، والباقي بالتناوب بين
   * الرئيس والجانبيّ — والمستخدمُ يعيد ترتيبها كما يشاء.
   */
  const defs = useMemo<SectionDef[]>(() => {
    let side = false;
    return cards.map((c) => {
      if (c.wide) return { key: c.key, title: c.title, column: 'full' as const };
      side = !side;
      return { key: c.key, title: c.title, column: side ? ('main' as const) : ('side' as const) };
    });
  }, [cards]);

  const layout = useProfileLayout(storageKey, defs);

  const withRange = (c: BoardCard): ReportDefinition =>
    c.ignoreRange ? c.definition : { ...c.definition, ...range };

  const render = (c: BoardCard) => (
    <Section key={c.key} layoutKey={c.key}>
      <ReportCard
        title={c.title}
        hint={c.hint}
        definition={withRange(c)}
        savedId={c.savedId}
        compact
      />
    </Section>
  );

  const byColumn = (col: SectionDef['column']) =>
    cards.filter((c) => defs.find((d) => d.key === c.key)?.column === col);

  return (
    <div className="dash-board">
      <div className="dash-bar">
        {title && <h2 className="card-title">{title}</h2>}
        <select
          className="select select-sm"
          value={days}
          onChange={(e) => setDays(e.target.value)}
          aria-label="المدة"
        >
          <option value="30">آخر ثلاثين يومًا</option>
          <option value="90">آخر ثلاثة أشهر</option>
          <option value="365">آخر سنة</option>
          <option value="1095">آخر ثلاث سنوات</option>
        </select>
        <ProfileLayoutButton api={layout} />
        <Link to={addTo} className="btn btn-sm btn-primary">
          <Icon name="plus" size={14} />
          تقرير جديد
        </Link>
      </div>

      {cards.length === 0 ? (
        <p className="small mute">لا بطاقات بعد — ابنِ تقريرًا في الباني وانشره على هذه اللوحة.</p>
      ) : (
        <ProfileLayoutProvider api={layout}>
          <ProfileLayoutBar api={layout} />
          <LayoutColumn api={layout} column="full">
            {byColumn('full').map(render)}
          </LayoutColumn>
          <div className="two-col pl-two">
            <LayoutColumn api={layout} column="main">
              {byColumn('main').map(render)}
            </LayoutColumn>
            <LayoutColumn api={layout} column="side">
              {byColumn('side').map(render)}
            </LayoutColumn>
          </div>
        </ProfileLayoutProvider>
      )}
    </div>
  );
}
